import React from 'react';
import { useTranslation } from 'react-i18next';
import { Business, LocationOn } from '@mui/icons-material';
import styles from './ConfirmStep.module.scss';

const SOCIAL_ICONS = {
    facebook: '📘',
    twitter: '🐦',
    instagram: '📸',
    youtube: '▶️',
    pinterest: '📌',
    linkedin: '💼',
};

const DAY_SHORT_KEYS = [
    'day_short_mon',
    'day_short_tue',
    'day_short_wed',
    'day_short_thu',
    'day_short_fri',
    'day_short_sat',
    'day_short_sun',
];

const InfoItem = ({ label, value }) => (
    <div className={styles.infoItem}>
        <label>{label}</label>
        {value ? <span>{value}</span> : <span className={styles.empty}>—</span>}
    </div>
);

const ConfirmStep = ({ step1Data, step2Data }) => {
    const { t } = useTranslation();

    if (!step1Data) return null;

    const contacts = [];
    if (step1Data.phone?.length) contacts.push({ label: t('phone'), values: step1Data.phone });
    if (step1Data.email?.length) contacts.push({ label: t('email'), values: step1Data.email });
    if (step1Data.website?.length) contacts.push({ label: t('website'), values: step1Data.website });

    const socials = ['facebook', 'twitter', 'instagram', 'youtube', 'pinterest', 'linkedin'].filter(
        (key) => step1Data[`${key}State`] && step1Data[key]
    );

    const locations = step2Data?.locations || [];
    const tags = [
        ...(step1Data.tagsService || []).map((v) => ({ label: v, type: 'service' })),
        ...(step1Data.tagsBrand || []).map((v) => ({ label: v, type: 'brand' })),
    ];

    return (
        <div className={styles.wrap}>
            {/* Identity */}
            <p className={styles.sectionTitle}>{t('company.about')}</p>
            <div className={styles.card}>
                <div className={styles.identityRow}>
                    <div className={styles.logoWrap}>
                        {step1Data.logoPreview ? (
                            <img
                                src={step1Data.logoPreview}
                                alt={step1Data.name}
                                onError={(e) => { e.target.onerror = null; e.target.src = '/spara-placeholder.png'; }}
                            />
                        ) : (
                            <span className={styles.logoPlaceholder}><Business /></span>
                        )}
                    </div>
                    <div className={styles.identityInfo}>
                        <h2>{step1Data.name || <span className={styles.empty}>—</span>}</h2>
                        <div className={styles.kennitala}>{step1Data.kennitala || '—'}</div>
                        {step1Data.type && (
                            <span
                                className={styles.typeBadge}
                                data-type={String(step1Data.type).toLowerCase()}
                            >
                                {step1Data.type}
                            </span>
                        )}
                    </div>
                </div>

                {tags.length > 0 && (
                    <div className={styles.tagList} style={{ marginTop: 16 }}>
                        {tags.map((tag, i) => (
                            <span key={i} className={styles.tag}>{tag.label}</span>
                        ))}
                    </div>
                )}
            </div>

            {/* Contact */}
            {contacts.length > 0 && (
                <>
                    <p className={styles.sectionTitle}>{t('contact')}</p>
                    <div className={styles.card}>
                        <div className={styles.infoGrid}>
                            {contacts.map((c, i) => (
                                <InfoItem
                                    key={i}
                                    label={c.label}
                                    value={c.values.join(', ')}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Description */}
            {(step1Data.description || step1Data.description_en) && (
                <>
                    <p className={styles.sectionTitle}>{t('description')}</p>
                    <div className={styles.card}>
                        {step1Data.description && (
                            <div style={{ marginBottom: step1Data.description_en ? 12 : 0 }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999ca4', marginBottom: 6 }}>IS</div>
                                <p className={styles.descriptionText}>{step1Data.description}</p>
                            </div>
                        )}
                        {step1Data.description_en && (
                            <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999ca4', marginBottom: 6 }}>EN</div>
                                <p className={styles.descriptionText}>{step1Data.description_en}</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Social */}
            {socials.length > 0 && (
                <>
                    <p className={styles.sectionTitle}>{t('social_media')}</p>
                    <div className={styles.card}>
                        <div className={styles.socialRow}>
                            {socials.map((key) => (
                                <a
                                    key={key}
                                    href={step1Data[key]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.socialBadge}
                                >
                                    {SOCIAL_ICONS[key]} {key.charAt(0).toUpperCase() + key.slice(1)}
                                </a>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Locations */}
            <p className={styles.sectionTitle}>{t('company.locations')}</p>
            {locations.length === 0 ? (
                <div className={styles.card}>
                    <div className={styles.noLocations}>{t('no_locations')}</div>
                </div>
            ) : (
                locations.map((loc, i) => {
                    const activeDays = (loc.torgid_company_openings || [])
                        .flatMap(schedule => schedule.filter(d => d.active));
                    return (
                        <div key={i} className={styles.locationCard}>
                            <div className={styles.locationHeader}>
                                <div className={styles.locationIcon}>
                                    <LocationOn fontSize="small" />
                                </div>
                                <div>
                                    <div className={styles.locationName}>
                                        {loc.name || [loc.address, loc.postal_code ? `${loc.postal_code} ${loc.city}` : loc.city].filter(Boolean).join(', ') || `${t('location')} ${i + 1}`}
                                    </div>
                                    {loc.address && (
                                        <div className={styles.locationAddress}>
                                            {[loc.address, loc.postal_code, loc.city].filter(Boolean).join(', ')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {activeDays.length > 0 && (
                                <div className={styles.openingHours}>
                                    <div className={styles.openingHoursLabel}>
                                        {t('opening_hours')}
                                    </div>
                                    <div className={styles.openingGrid}>
                                        {activeDays.map((day, di) => (
                                            <div key={di} className={styles.openingRow}>
                                                <span className={styles.dayName}>
                                                    {t(DAY_SHORT_KEYS[day.day] || day.day_txt)}
                                                </span>
                                                <span className={styles.dayTime}>
                                                    {day.active24
                                                        ? t('all_day')
                                                        : `${day.open_time} – ${day.close_time}`}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default ConfirmStep;
